import { api } from '@/lib/api'
import type {
  ApAgingReport,
  ApSummary,
  BillDetail,
  BillListItem,
  BillPayload,
  Vendor,
  VendorBankAccount,
  VendorPayload,
} from './types'

type PendingApproval = {
  pending_approval: true
  approval_instance_id: number
  message: string
}

export const apApi = {
  // Vendors
  listVendors: (params?: { search?: string; isActive?: boolean }) => {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.isActive !== undefined) qs.set('isActive', String(params.isActive))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<{ vendors: Vendor[] }>(`/api/invoicing/vendors${suffix}`)
  },
  getVendor: (id: number) => api.get<{ vendor: Vendor }>(`/api/invoicing/vendors/${id}`),
  createVendor: (body: VendorPayload) =>
    api.post<{ vendor: Vendor }>('/api/invoicing/vendors', body),
  updateVendor: (id: number, body: VendorPayload) =>
    api.put<{ vendor: Vendor }>(`/api/invoicing/vendors/${id}`, body),
  deactivateVendor: (id: number) =>
    api.del<{ vendor: Vendor }>(`/api/invoicing/vendors/${id}`),

  // Bank accounts
  listBankAccounts: (vendorId: number) =>
    api.get<{ bank_accounts: VendorBankAccount[] }>(`/api/invoicing/vendors/${vendorId}/bank-accounts`),
  upsertBankAccount: (vendorId: number, body: Partial<VendorBankAccount>) =>
    api.post<{ bank_account: VendorBankAccount }>(`/api/invoicing/vendors/${vendorId}/bank-accounts`, body),

  // Bills
  listBills: (params?: { status?: string; vendor_id?: number }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.vendor_id) qs.set('vendor_id', String(params.vendor_id))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<{ bills: BillListItem[] }>(`/api/invoicing/bills${suffix}`)
  },
  getBill: (id: number) => api.get<{ bill: BillDetail }>(`/api/invoicing/bills/${id}`),
  createBill: (body: BillPayload) =>
    api.post<{ bill: BillDetail }>('/api/invoicing/bills', body),
  submitBill: (id: number) =>
    api.post<{ bill: BillDetail } | PendingApproval>(`/api/invoicing/bills/${id}/submit`),
  postBill: (id: number) =>
    api.post<{ bill: BillDetail }>(`/api/invoicing/bills/${id}/post`),
  payBill: (id: number, body: {
    amount: number
    paid_at?: string
    method?: string
    reference?: string
    notes?: string
    send_email?: boolean
  }) =>
    api.post<{ payment_id: number; final_status: string; email_sent?: boolean }>(`/api/invoicing/bills/${id}/pay`, body),

  // Bill PDF attachment upload (multipart). Mirrors the invoice logo
  // upload flow - bypass the JSON wrapper, attach the Bearer token by
  // hand, retry once on 401 via tokenManager.
  uploadBillAttachment: async (file: File): Promise<{ key: string; filename: string; content_type: string; size: number }> => {
    const fd = new FormData()
    fd.append('file', file)
    const { tokenManager } = await import('@/lib/tokenManager')
    const { ApiError } = await import('@/lib/api')
    const send = async () => {
      const token = tokenManager.get()
      return fetch('/api/invoicing/bills/upload-attachment', {
        method: 'POST',
        credentials: 'same-origin',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      })
    }
    let res = await send()
    if (res.status === 401) {
      if (await tokenManager.refreshNow()) res = await send()
    }
    if (!res.ok) {
      let body: unknown
      try { body = await res.json() } catch { /* ignore */ }
      throw new ApiError(`Bill attachment upload failed (${res.status})`, res.status, body)
    }
    return res.json()
  },

  // Returns a 5-minute presigned S3 URL for the bill's attachment.
  attachmentUrl: (id: number) =>
    api.get<{ url: string }>(`/api/invoicing/bills/${id}/attachment-url`),

  // AP summary (Dashboard widget + platform admin tile).
  // Optional from/to window mirrors the AR side - the dashboard's
  // "paid in period" + recent activity respect the selected range.
  apSummary: (window?: { from: string; to: string }) => {
    const qs = new URLSearchParams()
    if (window?.from && window?.to) {
      qs.set('from', window.from)
      qs.set('to', window.to)
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<ApSummary>(`/api/invoicing/stats/ap-summary${suffix}`)
  },

  // AP Aging report
  apAging: (params?: { as_of?: string }) => {
    const qs = new URLSearchParams()
    if (params?.as_of) qs.set('as_of', params.as_of)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<{ report: ApAgingReport }>(`/api/invoicing/reports/ap-aging${suffix}`)
  },

  // Phase 5 - AI extraction. Mirrors the bank-statement upload flow:
  //   1. uploadBillAttachment - pushes the PDF to S3, returns a key.
  //   2. startBillExtraction(key) - kicks off async extraction on the
  //      server, returns `{ extraction_id }` in <100ms.
  //   3. pollBillExtraction(id) - hit repeatedly until status is
  //      'ready' or 'failed'. The server updates the row when the
  //      background promise finishes.
  startBillExtraction: (attachment_s3_key: string, pages_per_chunk = 5) =>
    api.post<{ extraction_id: number; status: 'pending' }>(
      '/api/invoicing/bills/extract-attachment',
      { attachment_s3_key, pages_per_chunk },
    ),
  pollBillExtraction: (id: number) =>
    api.get<{
      id: number
      status: 'pending' | 'ready' | 'failed' | 'reviewed' | 'applied'
      error: string | null
      extracted_fields: {
        vendor_name?: string | null
        vendor_email?: string | null
        vendor_phone?: string | null
        vendor_address_line1?: string | null
        vendor_address_line2?: string | null
        vendor_city?: string | null
        vendor_state_region?: string | null
        vendor_postal_code?: string | null
        vendor_country?: string | null
        vendor_tax_id?: string | null
        vendor_bank_name?: string | null
        vendor_bank_account_number?: string | null
        vendor_bank_routing_number?: string | null
        vendor_bank_iban?: string | null
        vendor_bank_swift?: string | null
        vendor_bank_account_holder_name?: string | null
        vendor_invoice_number?: string | null
        issue_date?: string | null
        due_date?: string | null
        currency?: string | null
        subtotal?: number | null
        tax_total?: number | null
        total?: number | null
        discount?: number | null
        discount_label?: string | null
        shipping_amount?: number | null
        shipping_label?: string | null
        header_tax_rate_pct?: number | null
        notes?: string | null
        terms?: string | null
      }
      extracted_lines: Array<{
        line_number?: number
        description?: string
        quantity?: number | null
        unit_price?: number | null
        amount?: number | null
        tax_rate?: number | null
        suggested_account_code?: string | null
      }>
      field_confidence: Record<string, number>
      overall_confidence: number | null
      vendor_match: { id: number; display_name: string } | null
    }>(`/api/invoicing/bills/extract-status/${id}`),
}
